/**
 * Purpose:
 *   Renders an expandable menu item with submenu.
 */

import { cx } from '@/util/functions/cx';
import { useMenuItem } from './(hooks)/useMenuItem';
import { MenuItemIcon } from './MenuItemIcon';
import { MenuItemText } from './MenuItemText';
import { MenuItemChevron } from './MenuItemChevron';
import { MenuItemSubmenu } from './MenuItemSubmenu';
import { CollapsedMenuItemPopover } from './CollapsedMenuItemPopover';
import { useCollapsedMenuPopover } from './(hooks)/useCollapsedMenuPopover';
import {
  MENU_ITEM_EXPANDABLE_WRAPPER,
  MENU_ITEM_EXPANDABLE_WRAPPER_ACTIVE,
  MENU_ITEM_EXPANDABLE_HEADER,
  MENU_ITEM_ACTIVE,
  MENU_TEXT,
  MENU_TEXT_ACTIVE,
} from './Menu.styles';
import type { MenuItemConfig } from '@/types/menu';

export interface IExpandableMenuItemProps {
  item: MenuItemConfig;
  isExpanded: boolean;
  isActive: boolean;
  isParentActive: boolean;
  onToggle: () => void;
  onNavigate: (href: string) => void;
  isChildActive: (item: MenuItemConfig) => boolean;
  onLinkClick?: () => void;
  isCollapsed: boolean;
}

export function ExpandableMenuItem({
  item,
  isExpanded,
  isActive,
  isParentActive,
  onToggle,
  onNavigate,
  isChildActive,
  onLinkClick,
  isCollapsed,
}: IExpandableMenuItemProps) {
  const {
    popoverRef,
    isOpen,
    canPortal,
    position,
    setAnchorElement,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
  } = useCollapsedMenuPopover();
  const {
    showActiveState,
    effectiveExpanded,
    containerStyle,
    textStyle,
    chevronContainerStyle,
    submenuStyle,
    iconContainerStyle,
    submenuRef,
  } = useMenuItem({
    item,
    isExpanded,
    isActive,
    isParentActive,
    isCollapsed,
  });

  const wrapperClasses = cx(
    MENU_ITEM_EXPANDABLE_WRAPPER,
    effectiveExpanded && MENU_ITEM_EXPANDABLE_WRAPPER_ACTIVE
  );

  const headerClasses = cx(
    MENU_ITEM_EXPANDABLE_HEADER,
    showActiveState && !effectiveExpanded && MENU_ITEM_ACTIVE
  );

  const textClasses = showActiveState ? MENU_TEXT_ACTIVE : MENU_TEXT;
  const submenuId = `menu-submenu-${item.id}`;
  const buttonId = `menu-trigger-${item.id}`;

  return (
    <div className={wrapperClasses}>
      <button
        id={buttonId}
        ref={setAnchorElement}
        type="button"
        onClick={isCollapsed ? undefined : onToggle}
        className={headerClasses}
        style={containerStyle}
        aria-label={item.label}
        aria-expanded={isCollapsed ? undefined : effectiveExpanded}
        aria-controls={isCollapsed ? undefined : submenuId}
        onMouseEnter={isCollapsed ? handleMouseEnter : undefined}
        onMouseLeave={isCollapsed ? handleMouseLeave : undefined}
        onFocus={isCollapsed ? handleFocus : undefined}
        onBlur={isCollapsed ? handleBlur : undefined}
      >
        <MenuItemIcon icon={item.icon} style={iconContainerStyle} />
        <MenuItemText
          text={item.label}
          className={textClasses}
          style={textStyle}
        />
        <MenuItemChevron
          expanded={effectiveExpanded}
          style={chevronContainerStyle}
        />
      </button>
      {isCollapsed ? (
        <CollapsedMenuItemPopover
          item={item}
          isOpen={isOpen}
          canPortal={canPortal}
          position={position}
          popoverRef={popoverRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
          isChildActive={isChildActive}
          onNavigate={onNavigate}
          onLinkClick={onLinkClick}
        />
      ) : null}
      <MenuItemSubmenu
        item={item}
        isExpanded={effectiveExpanded}
        isChildActive={isChildActive}
        onNavigate={onNavigate}
        onLinkClick={onLinkClick}
        submenuRef={submenuRef}
        buttonId={buttonId}
        submenuId={submenuId}
        style={submenuStyle}
      />
    </div>
  );
}
